/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { getPayloadObject, getTelemetryAttributes, IPayLoad, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

/**
 * RefinerItemComponent Interface.
 */
export interface IRefinerItemComponent extends IComponent<IRefineItemProps> {}

/**
 * IRefineItem props Interface.
 */
export interface IRefineItemProps extends IComponentProps<{}> {
    isDisabled?: boolean;
    refineItemUrl?: string;
    isSingleSelect: boolean;
    label: string;
    isChecked?: boolean;
    className?: string;
    refineItemAriaLabel?: string;
    children?: JSX.Element | undefined;
    telemetryContent?: ITelemetryContent;
    onClick(): void;
}

/**
 * Refiner Item component.
 * @param props - IRefineItemProps.
 * @returns - Renders the Refiner Item.
 */
const RefinerItemFunctionalComponent: React.FC<IRefineItemProps> = (props: IRefineItemProps) => {
    const { isDisabled, refineItemAriaLabel, refineItemUrl, isSingleSelect, label, children, isChecked, onClick, telemetryContent } = props;

    let itemTypeClassName = isSingleSelect ? 'single-select' : 'multi-select';
    itemTypeClassName = `ms-refine-submenu-item ${itemTypeClassName}`;
    const inputType = isSingleSelect ? 'radio' : 'checkbox';
    itemTypeClassName = isChecked ? `${itemTypeClassName}-checked` : itemTypeClassName;
    itemTypeClassName = isDisabled ? `${itemTypeClassName} disabled` : itemTypeClassName;
    const payLoad: IPayLoad = getPayloadObject('click', telemetryContent!, '');
    payLoad.contentAction.etext = label;
    const refineItemAttributes = getTelemetryAttributes(telemetryContent!, payLoad);

    /**
     * OnClickHandler Method.
     * @param event - Mouse event.
     */
    const onClickHandler = (event: React.MouseEvent<HTMLAnchorElement | HTMLInputElement>): void => {
        event.preventDefault();
        !props.isDisabled && onClick();
    };

    return (
        <a
            href={refineItemUrl}
            tabIndex={0}
            onClick={onClickHandler}
            className={itemTypeClassName}
            role={inputType}
            aria-label={refineItemAriaLabel ?? `${label}`}
            aria-checked={isChecked}
            data-testid='testid-refine-item-container'
            {...refineItemAttributes}
        >
            {children}
            <span className='ms-refine-submenu-item__label' aria-hidden='true' data-testid='testid-refine-item'>
                {label}
            </span>
        </a>
    );
};

/**
 * Refiner Item Component.
 */
// @ts-expect-error -- Required for this signature.
export const RefinerItemComponent: React.FunctionComponent<IRefineItemProps> = msdyn365Commerce.createComponent<IRefinerItemComponent>(
    'Refiner-Item',
    { component: React.memo(RefinerItemFunctionalComponent) }
);
